import {
    EventOutlined,
    FlagOutlined,
    GroupsOutlined,
    HourglassEmptyOutlined,
    PersonOutlineRounded,
    ScheduleOutlined,
    TrendingUpOutlined
} from '@mui/icons-material';
import moment from 'moment';
import { IntlShape, WrappedComponentProps, injectIntl } from 'react-intl';
import { getSplitBreaks, getSplitBreaksLabelId } from '../../libs/lessonSplit';
import { LESSON_FILTERS, OPTIONS_DEPENDENCES, QUESTIONS_TYPES } from '../../resources/constants';
import { decimalToTime, getLessonNameFromType } from '../../resources/utils';
import { LessonCart, ParticipantInfo } from '../../types/booking';
import LessonCard, { InfoCell, OptionRow } from './lessonCard';

/**
 * Cart lesson card - maps a `LessonCart` into the shared `LessonCard` view-model.
 */
interface IProps {
    lesson: LessonCart;
    disabled: boolean;
    onDelete(event: any, lesson: LessonCart): void;
    // eslint-disable-next-line react/no-unused-prop-types
    type: string;
}

type Props = IProps & WrappedComponentProps;

function num(value: number | string | undefined): number {
    if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return Number.isFinite(value as number) ? (value as number) : 0;
}

function participantName(p: ParticipantInfo): string {
    const first = (p.firstname ?? p.Firstname ?? '') as string;
    const last = (p.name ?? p.Name ?? '') as string;
    return `${first} ${last}`.trim();
}

function participantInitials(p: ParticipantInfo): string {
    const first = String(p.firstname ?? p.Firstname ?? '');
    const last = String(p.name ?? p.Name ?? '');
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    return initials || '–';
}

// Participant fields are dynamic (the school's `inputParticipant` config decides
// which keys each participant carries). We render only the fields that actually
// have a value, label them via the `booking.participant.<key>` namespace, and
// drop name/firstname/ids (name is the primary cell). zip + town collapse into a
// single "Provenance" column.
const IGNORED_PARTICIPANT_KEYS = new Set(['p_id', 'id', 'firstname', 'name', 'zip', 'town', 'languageid', 'countryid']);
const PARTICIPANT_LABEL_ALIAS: Record<string, string> = { language: 'languageid', country: 'countryid' };
const PARTICIPANT_FIELD_ORDER = ['provenance', 'birthdate', 'age', 'sex', 'address', 'addresslocal', 'phone', 'mobile', 'email', 'country', 'language', 'weight', 'height', 'footsize', 'avs'];

function participantFieldOrder(key: string): number {
    const i = PARTICIPANT_FIELD_ORDER.indexOf(key);
    return i === -1 ? PARTICIPANT_FIELD_ORDER.length : i;
}

function humanizeKey(key: string): string {
    const s = key.replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export function buildParticipants(infos: any[], intl: IntlShape) {
    const colMap = new Map<string, string>();
    const rows = (infos || []).map((p) => {
        const values: Record<string, string> = {};
        const prov = `${(p as any).zip ?? (p as any).Zip ?? ''} ${(p as any).town ?? (p as any).Town ?? ''}`.trim();
        if (prov) {
            values.provenance = prov;
            if (!colMap.has('provenance')) colMap.set('provenance', intl.formatMessage({ id: 'cart.provenance' }));
        }
        Object.keys(p).forEach((rawKey) => {
            const key = rawKey.toLowerCase();
            if (IGNORED_PARTICIPANT_KEYS.has(key)) return;
            let value: any = (p as any)[rawKey];
            if (value === null || value === undefined || value === '' || value === 0) return;
            if (key === 'birthdate') {
                if (typeof value === 'string' && value.startsWith('0001-01-01')) return;
                const m = moment(value);
                if (!m.isValid()) return;
                value = m.format('DD.MM.YYYY');
            } else if (typeof value === 'object') {
                // select fields are stored as { value, label } (e.g. sex); other
                // nested objects (language/country) carry { name }.
                value = value.label ?? value.name ?? value.value;
            }
            if (value === null || value === undefined || value === '' || value === 0) return;
            values[key] = String(value);
            if (!colMap.has(key)) {
                const labelKey = PARTICIPANT_LABEL_ALIAS[key] || key;
                colMap.set(key, intl.formatMessage({ id: `booking.participant.${labelKey}`, defaultMessage: humanizeKey(key) }));
            }
        });
        return { initials: participantInitials(p), name: participantName(p) || '-', values };
    });
    const columns = Array.from(colMap.entries())
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => participantFieldOrder(a.key) - participantFieldOrder(b.key));
    return { columns, rows };
}

function CartLesson(props: Props) {
    const {
        lesson, disabled, onDelete, intl
    } = props;
    const isGroup = lesson.lesson_type === LESSON_FILTERS.GROUP_LESSON;
    const handleRemove = (event: any) => onDelete(event, lesson);

    // ----- price (course + options breakdown) -----
    const optionsPrice = num(lesson.totalOptionsPrice);
    const total = optionsPrice + lesson.price;
    const breakdown = optionsPrice > 0
        ? `${intl.formatMessage({ id: 'cart.priceCourse' })} ${lesson.price.toFixed(2)} + ${intl.formatMessage({ id: 'cart.priceOptions' })} ${optionsPrice.toFixed(2)}`
        : undefined;

    // ----- hours (per day + total), reused from the original logic -----
    let hours = '';
    let totalHours = '';
    if (isGroup && typeof lesson.hours !== 'string') {
        hours = decimalToTime(lesson.hours.hoursLesson, intl.formatMessage) + intl.formatMessage({ id: 'booking.hoursDay' });
        totalHours = decimalToTime(lesson.hours.totalHoursLesson, intl.formatMessage, false) + intl.formatMessage({ id: 'booking.hoursTotal' });
    } else if (typeof lesson.hours === 'string') {
        const time = moment.duration(lesson.hours.replace('h', ':')).asHours() * lesson.days;
        hours = lesson.hours + intl.formatMessage({ id: 'booking.hoursDay' });
        totalHours = `${decimalToTime(time, intl.formatMessage, false)} ${intl.formatMessage({ id: 'booking.hoursTotal' })}`;
    }

    const splitBreaks = lesson.time.to_time.durationSplit ? getSplitBreaks(lesson.time.to_time.split) : [];

    // ----- info grid -----
    const info: InfoCell[] = [
        {
            icon: <EventOutlined />,
            label: intl.formatMessage({ id: 'booking.startDate' }),
            value: `(${moment(lesson.date.from_date).format('dd')}) ${moment(lesson.date.from_date).format('DD.MM.YYYY')}`,
        },
    ];
    if (isGroup) {
        info.push({
            icon: <EventOutlined />,
            label: intl.formatMessage({ id: 'booking.endDate' }),
            value: `(${moment(lesson.date.to_date).format('dd')}) ${moment(lesson.date.to_date).format('DD.MM.YYYY')}`,
        });
        info.push({ icon: <ScheduleOutlined />, label: intl.formatMessage({ id: 'booking.startTime' }), value: moment(lesson.time.from_time).format('HH[:]mm') });
        info.push({ icon: <ScheduleOutlined />, label: intl.formatMessage({ id: 'booking.endTime' }), value: moment(lesson.time.to_time.time).format('HH[:]mm') });
    } else {
        info.push({ icon: <EventOutlined />, label: intl.formatMessage({ id: 'booking.daysLength' }), value: `${lesson.days} ${intl.formatMessage({ id: `booking.day${lesson.days > 1 ? 's' : ''}` })}` });
        info.push({ icon: <ScheduleOutlined />, label: intl.formatMessage({ id: 'booking.startTime' }), value: moment(lesson.time.from_time).format('HH[:]mm') });
    }
    if (splitBreaks.length > 0) {
        info.push({ icon: <ScheduleOutlined />, label: intl.formatMessage({ id: getSplitBreaksLabelId(splitBreaks) }), value: splitBreaks.join(', ') });
    }
    if (hours) {
        info.push({
            icon: <HourglassEmptyOutlined />, label: intl.formatMessage({ id: 'booking.hours' }), value: hours, sub: totalHours
        });
    }
    info.push({
        icon: <TrendingUpOutlined />,
        label: intl.formatMessage({ id: 'booking.level' }),
        value: (lesson.level?.name?.length || 0) > 0 ? lesson.level?.name : intl.formatMessage({ id: 'booking.noLevel' }),
    });
    info.push({
        icon: <FlagOutlined />,
        label: intl.formatMessage({ id: 'booking.meeting' }),
        value: lesson.meeting_point.name,
        badge: lesson.meeting_point.memo || undefined,
    });
    if (lesson.instructor) {
        info.push({
            icon: <PersonOutlineRounded />,
            label: intl.formatMessage({ id: 'booking.freeInstructors' }),
            value: `${lesson.instructor.Firstname ?? ''} ${lesson.instructor.Name ?? ''}`.trim(),
        });
    }

    // ----- participants (dynamic fields, empty ones hidden) -----
    const { columns: participantColumns, rows: participantRows } = buildParticipants(lesson.participants_infos, intl);
    const expected = parseInt(String(lesson.participants), 10);
    const participants = participantRows.length > 0 || expected > 0
        ? {
            count: participantRows.length,
            total: Number.isFinite(expected) ? expected : undefined,
            columns: participantColumns,
            rows: participantRows,
        }
        : undefined;
    const incomplete = Number.isFinite(expected) && lesson.participants_infos.length !== expected;

    // ----- options -----
    const optRows: OptionRow[] = lesson.options.map((o) => {
        const isPercent = o.type === OPTIONS_DEPENDENCES.PERCENT;
        return {
            text: o.text,
            description: o.description || undefined,
            required: o.obligatory,
            qty: o.qty > 0 ? o.qty : undefined,
            unit: isPercent ? `${o.price}%` : num(o.price).toFixed(2),
            total: num(o.total).toFixed(2),
        };
    });
    const options = optRows.length > 0
        ? { rows: optRows, subtotal: `CHF ${optionsPrice.toFixed(2)}` }
        : undefined;

    // ----- questions / informations complémentaires (previously never rendered) -----
    const formatAnswer = (q: { type: string; answer?: string; }): string => {
        const a = q.answer ?? '';
        if (!a) return '';
        if (q.type === QUESTIONS_TYPES.DATE) return moment(a).isValid() ? moment(a).format('DD.MM.YYYY') : a;
        if (q.type === QUESTIONS_TYPES.DATETIME) return moment(a).isValid() ? moment(a).format('DD.MM.YYYY HH[:]mm') : a;
        if (q.type === QUESTIONS_TYPES.TIME) return moment(a, ['HH:mm:ss', 'HH:mm', moment.ISO_8601]).isValid() ? moment(a, ['HH:mm:ss', 'HH:mm', moment.ISO_8601]).format('HH[:]mm') : a;
        if (q.type === QUESTIONS_TYPES.BOOLEAN) return intl.formatMessage({ id: a === 'true' || a === '1' ? 'yes' : 'no' });
        return a;
    };
    const questions = (lesson.questions || [])
        .filter((q) => q.answer !== undefined && q.answer !== null && String(q.answer).length > 0)
        .map((q) => ({ q: q.text, a: formatAnswer(q) }));

    // ----- problem state (unavailable, price gone, or incomplete participants) -----
    let problem;
    if (lesson.priceUnavailable) {
        problem = { reason: intl.formatMessage({ id: 'cart.reasonPriceUnavailable' }), severity: 'danger' as const };
    } else if (disabled) {
        problem = { reason: intl.formatMessage({ id: 'cart.reasonUnavailable' }), severity: 'danger' as const };
    } else if (incomplete) {
        problem = { reason: intl.formatMessage({ id: 'cart.reasonIncomplete' }), severity: 'warn' as const };
    }

    return (
        <LessonCard
            intl={intl}
            model={{
                typePill: getLessonNameFromType(lesson.lesson_type, intl.formatMessage),
                typePillIcon: isGroup ? <GroupsOutlined /> : <PersonOutlineRounded />,
                durationPill: `${lesson.days}${intl.formatMessage({ id: `booking.day${lesson.days > 1 ? 's' : ''}` })}`,
                title: lesson.title,
                price: { total: `CHF ${total.toFixed(2)}`, breakdown },
                onRemove: handleRemove,
                problem,
                disabled,
                info,
                participants,
                options,
                questions: questions.length > 0 ? questions : undefined,
                remark: typeof lesson.remarks === 'string' && lesson.remarks ? lesson.remarks : undefined,
            }}
        />
    );
}

export default injectIntl(CartLesson);
